import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Linkedin, Twitter, Home as HomeIcon, Code, Mail, ArrowRight, Zap, Tv, Database, TouchpadIcon, Newspaper } from 'lucide-react';
import MinimalProjectCard from '../components/MinimalProjectCard';
import { projectsData } from '../data/projectsData';
import '../styles/Home.css';

const Home: React.FC = () => {
  const socialLinks = [
    { icon: Linkedin, url: 'https://www.linkedin.com/in/psugasti/', label: 'LinkedIn' },
    { icon: Twitter, url: 'http://twitter.com/psugasti', label: 'Twitter' },
  ];

  return (
    <>
      <Helmet>
        <title>Pedro Sugasti - Broadcast Specialist & Creative Developer</title>
        <meta name="description" content="Pedro Sugasti is a broadcast specialist and creative developer based in Berlin. Specializing in real-time graphics, data visualization, and interactive systems for major broadcasters." />
        
        {/* Open Graph tags for social media */}
        <meta property="og:title" content="Pedro Sugasti - Broadcast Specialist & Creative Developer" />
        <meta property="og:description" content="Broadcast specialist and creative developer based in Berlin. Specializing in real-time graphics, data visualization, and interactive systems for major broadcasters." />
        <meta property="og:image" content="https://pedrosugasti.com/profile-photo.jpg" />
        <meta property="og:url" content="https://pedrosugasti.com" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pedro Sugasti - Broadcast Specialist" />
        <meta name="twitter:description" content="Broadcast specialist and creative developer based in Berlin. Real-time graphics and interactive systems for major broadcasters." />
        <meta name="twitter:image" content="https://pedrosugasti.com/profile-photo.jpg" />
        
        {/* Additional SEO tags */}
        <meta name="author" content="Pedro Sugasti" />
        <meta name="keywords" content="broadcast graphics, real-time graphics, data visualization, interactive systems, Vizrt, broadcast automation, TV graphics, Berlin" />
        <link rel="canonical" href="https://pedrosugasti.com" />
      </Helmet>
      
      <div className="home">
      <section className="hero-split-section">
        <div className="hero-left-half">
          <div className="circular-image-container">
            <img 
              src="/profile-photo.jpg" 
              alt="Pedro Sugasti" 
              className="circular-image"
            />
          </div>
        </div>
        <div className="hero-right-half">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="highlight">Pedro Sugasti</span>
          </motion.h1>
          <motion.h2 
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Broadcast Specialist
          </motion.h2>
          <motion.p 
            className="hero-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            Originally from Argentina 🇦🇷, I've spent more than a decade living and working in Germany 🇩🇪, where I've honed my skills in computer graphics and broadcasting. If you're in need of a creative mind to help you with your next project, look no further. Feel free to drop me a line using the Contact Form below, and let's make something great together!
          </motion.p>
          <motion.div 
            className="tech-stack"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <span className="tech-icon"><Code size={24} /></span>
            <span className="tech-icon"><Mail size={24} /></span>
          </motion.div>
          <motion.div 
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <Link to="/work" className="nav-button primary">
              <span className="nav-button-label">View Work</span>
              <span className="nav-button-icon"><ArrowRight size={16} /></span>
            </Link>
            <Link to="/contact" className="nav-button secondary">
              <span className="nav-button-label">Contact Form</span>
              <span className="nav-button-icon"><Mail size={16} /></span>
            </Link>
          </motion.div>
          <motion.div 
            className="social-links-hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            {socialLinks.map((link, index) => (
              <motion.a
                key={link.label}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <link.icon size={20} />
              </motion.a>
            ))}
          </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Latest Work Section */}
      <section className="highlighted-projects-section">
        <div className="highlighted-projects-container">
          <div className="highlighted-projects-header">
            <h2 className="projects-title">
              <Zap size={20} />
              Latest Work
            </h2>
          </div>
          <div className="highlighted-projects-grid">
            {projectsData
              .slice(0, 3) // Get the last 3 projects (they're already sorted by most recent)
              .map((project) => (
                <MinimalProjectCard key={project.id} project={project} />
              ))
            }
          </div>
        </div>
      </section>

      <section className="quick-intro">
        <div className="intro-grid">
          <motion.div 
            className="intro-card"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            whileHover={{ y: -10 }}
          >
            <div className="card-icon">
              <Tv size={40} />
            </div>
            <h3>Broadcast & Automation</h3>
            <p>Cutting-edge broadcast graphics and automation systems for news, sports, and live productions. Streamlining workflows for major TV networks worldwide</p>
            <Link to="/work?category=automation" className="card-link">
              View Work <span className="link-icon"><ArrowRight size={16} /></span>
            </Link>
          </motion.div>
          <motion.div 
            className="intro-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            whileHover={{ y: -10 }}
          >
            <div className="card-icon">
              <Database size={40} />
            </div>
            <h3>Data Visualization</h3>
            <p>Transforming complex data into compelling visual stories. Real-time analytics, statistics, and infographics for broadcast and digital platforms</p>
            <Link to="/work?category=data" className="card-link">
              View Work <span className="link-icon"><ArrowRight size={16} /></span>
            </Link>
          </motion.div>
          <motion.div 
            className="intro-card"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ y: -10 }}
          >
            <div className="card-icon">
              <TouchpadIcon size={40} />
            </div>
            <h3>Interactive Touch</h3>
            <p>Engaging touchscreen experiences for live broadcasts. Interactive analysis tools that bring viewers closer to the action with intuitive interfaces</p>
            <Link to="/work?category=touch" className="card-link">
              View Work <span className="link-icon"><ArrowRight size={16} /></span>
            </Link>
          </motion.div>
          <motion.div 
            className="intro-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            whileHover={{ y: -10 }}
          >
            <div className="card-icon">
              <Newspaper size={40} />
            </div>
            <h3>News & Sports</h3>
            <p>Specialized graphics packages for news and sports broadcasting. From breaking news to major sporting events, delivering impactful visual content</p>
            <Link to="/work?category=news" className="card-link">
              View Work <span className="link-icon"><ArrowRight size={16} /></span>
            </Link>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;